import { useState, useEffect } from 'react';
import { Label } from 'ui/Label';
import { Input } from 'ui/Input';
import { ListingProductCard } from 'ui/Card/ListingProductCard';
import { InputNumber } from 'ui/InputNumber';
import { Checkbox } from 'ui/Checkbox';
import { Button } from 'ui/Button';
import { Icon } from 'ui/Icon';
import { CategorySelector } from 'ui/Select/Selectors/CategorySelectors';
import { Select, SelectOption } from 'ui/Select';
import { useIntl } from 'react-intl';
import { Form, message } from 'antd';
import { StyledNewListingProductsForm } from './style';

import {
  FarmQualityMark,
  StorageUnit,
  NewListingProductsFormProps,
  ListingProduct,
  ProductFormValues,
  QualityMarkId,
} from '../../../types/ListingData';

export const NewListingProductsForm = ({
  onFormStateChange,
  onProductsChange,
  existingProducts = [],
  farmQualityMarks = [],
  storageUnits = [],
  hasValidationError = false,
}: NewListingProductsFormProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [products, setProducts] = useState<ListingProduct[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ListingProduct | null>(
    null
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [allowSmallerQuantity, setAllowSmallerQuantity] = useState(false);
  const [selectedStorageUnit, setSelectedStorageUnit] = useState<number | null>(
    storageUnits.length > 0
      ? storageUnits.find((unit) => unit.id === 1)?.id || storageUnits[0].id
      : null
  );
  const [selectedQualityMarks, setSelectedQualityMarks] = useState<
    QualityMarkId[]
  >([]);

  useEffect(() => {
    if (existingProducts && existingProducts.length > 0) {
      setProducts(existingProducts);
    } else if (
      existingProducts &&
      existingProducts.length === 0 &&
      products.length > 0
    ) {
      setProducts([]);
      form.resetFields();
      setIsEditMode(false);
      setEditingProduct(null);
      setAllowSmallerQuantity(false);
      setSelectedStorageUnit(null);
    }
  }, [existingProducts]);

  useEffect(() => {
    if (storageUnits.length > 0 && !selectedStorageUnit) {
      setSelectedStorageUnit(
        storageUnits.find((unit) => unit.id === 1)?.id || storageUnits[0].id
      );
    }
  }, [storageUnits, selectedStorageUnit]);

  const handleFormSubmit = (values: ProductFormValues) => {
    const getCategoryId = (
      category: number | { value: number; label: string } | undefined
    ) => (typeof category === 'object' ? category?.value : category);
    const getCategoryName = (
      category: number | { value: number; label: string } | undefined
    ) => (typeof category === 'object' ? category?.label : '');

    const categoryId =
      getCategoryId(values['sub-subcategory']) ||
      getCategoryId(values['sub-category']) ||
      getCategoryId(values['main-category']);

    const categoryName =
      getCategoryName(values['sub-subcategory']) ||
      getCategoryName(values['sub-category']) ||
      getCategoryName(values['main-category']);

    if (!categoryId) {
      return;
    }

    if (!isEditMode) {
      const isDuplicate = products.some(
        (product) => product.category_id === categoryId
      );

      if (isDuplicate) {
        message.error(
          intl.formatMessage({ id: 'listings.product_already_exists' })
        );
        return;
      }
    }

    const createCategoryObject = (categoryId: number, categoryName: string) => {
      if (isEditMode && editingProduct?.category?.route_path) {
        return {
          id: categoryId,
          name: categoryName,
          route_path: editingProduct.category.route_path,
        };
      }

      const primaryId = getCategoryId(values['main-category']);
      const secondaryId = getCategoryId(values['sub-category']);
      const tertiaryId = getCategoryId(values['sub-subcategory']);

      let routePath: {
        primary?: number;
        secondary?: number;
        tertiary?: number;
      } = {};

      if (primaryId) {
        routePath.primary = primaryId;
      }
      if (secondaryId) {
        routePath.secondary = secondaryId;
      }
      if (tertiaryId) {
        routePath.tertiary = tertiaryId;
      }

      return {
        id: categoryId,
        name: categoryName,
        route_path: routePath,
      };
    };

    const finalCategoryName =
      isEditMode && editingProduct
        ? editingProduct.category?.name || ''
        : categoryName;

    const selectedStorageUnitData = storageUnits.find(
      (unit: StorageUnit) => unit.id === selectedStorageUnit
    );

    const productName =
      values.name?.trim() ||
      (isEditMode && editingProduct?.name) ||
      categoryName;

    let productData: ListingProduct = {
      category_id: categoryId,
      name: productName || undefined,
      category: createCategoryObject(categoryId, finalCategoryName),
      required_quantity: Number(values.required_quantity) || 0,
      period_for_total_quantity: values.period_for_total_quantity || '',
      allow_smaller_quantity: allowSmallerQuantity,
      quality_mark_ids: selectedQualityMarks,
      storage_unit_id: selectedStorageUnit || undefined,
      storage_unit: selectedStorageUnitData
        ? {
            id: selectedStorageUnitData.id,
            name: selectedStorageUnitData.name,
            short: selectedStorageUnitData.short,
          }
        : undefined,
    };

    if (allowSmallerQuantity) {
      productData.min_acceptable_offer_quantity =
        Number(values.min_acceptable_offer_quantity) || 0;
    } else if (isEditMode && editingProduct) {
      productData.min_acceptable_offer_quantity =
        editingProduct.min_acceptable_offer_quantity;
    }

    let updatedProducts: ListingProduct[];

    if (isEditMode && editingProduct) {
      updatedProducts = products.map((product) =>
        product.category_id === editingProduct.category_id
          ? { ...product, ...productData, id: product.id }
          : product
      );
    } else {
      updatedProducts = [...products, productData];
    }

    setProducts(updatedProducts);
    setShowAddForm(false);
    setIsEditMode(false);
    setEditingProduct(null);
    setSelectedQualityMarks([]);
    onFormStateChange?.(false);
    onProductsChange?.(updatedProducts);
    form.resetFields();
  };

  const handleFormCancel = () => {
    form.resetFields();
    setShowAddForm(false);
    setIsEditMode(false);
    setEditingProduct(null);
    setAllowSmallerQuantity(false);
    setSelectedStorageUnit(null);
    setSelectedQualityMarks([]);
    onFormStateChange?.(false);
  };

  const handleOpenForm = () => {
    setShowAddForm(true);
    setIsEditMode(false);
    setEditingProduct(null);
    setAllowSmallerQuantity(false);
    setSelectedQualityMarks([]);
    setSelectedStorageUnit(
      storageUnits.length > 0
        ? storageUnits.find((unit) => unit.id === 1)?.id || storageUnits[0].id
        : null
    );
    form.resetFields();
    onFormStateChange?.(true);
  };

  const handleEditProduct = (productId: number) => {
    const productToEdit = products.find((p) => p.category_id === productId);

    if (!productToEdit) return;

    setEditingProduct(productToEdit);
    setIsEditMode(true);
    setShowAddForm(true);
    onFormStateChange?.(true);

    const qualityMarkValues = productToEdit.quality_mark_ids || [];

    setSelectedQualityMarks(qualityMarkValues);

    form.setFieldsValue({
      name: productToEdit.name || '',
      required_quantity: productToEdit.required_quantity,
      period_for_total_quantity: productToEdit.period_for_total_quantity || '',
      min_acceptable_offer_quantity:
        productToEdit.min_acceptable_offer_quantity || 0,
    });

    setAllowSmallerQuantity(productToEdit.allow_smaller_quantity);

    setSelectedStorageUnit(productToEdit.storage_unit_id || null);

    if (productToEdit.category?.route_path) {
      const routePath = productToEdit.category.route_path;

      if (routePath.tertiary) {
        form.setFieldsValue({
          'main-category': routePath.primary,
          'sub-category': routePath.secondary,
          'sub-subcategory': routePath.tertiary,
        });
      } else if (routePath.secondary) {
        form.setFieldsValue({
          'main-category': routePath.primary,
          'sub-category': routePath.secondary,
        });
      } else if (routePath.primary) {
        form.setFieldsValue({
          'main-category': routePath.primary,
        });
      }
    }
  };

  const handleDeleteProduct = (categoryId: number) => {
    const updatedProducts = products.filter(
      (p) => p.category_id !== categoryId
    );
    setProducts(updatedProducts);
    onProductsChange?.(updatedProducts);
  };

  return (
    <StyledNewListingProductsForm className={showAddForm ? 'form-overlay' : ''}>
      <div className={'products-items'}>
        {!showAddForm ? (
          <div className="cards-container">
            <ListingProductCard
              variant="add"
              onAdd={handleOpenForm}
              className={'add-product-card'}
            />

            {hasValidationError && products.length === 0 && (
              <Label
                label={intl.formatMessage({
                  id: 'listings.form.at_least_one_product_required',
                })}
                className="validation-error-message"
              />
            )}

            {products.length > 0
              ? products.map((product, index) => (
                  <ListingProductCard
                    key={product.category_id}
                    variant="existing"
                    product={{
                      name:
                        product.name ||
                        product.category?.name ||
                        `Category ${product.category_id}`,
                    }}
                    productNumber={index + 1}
                    quantity={product.required_quantity}
                    minQuantity={product.min_acceptable_offer_quantity}
                    unit={
                      product.storage_unit_id
                        ? storageUnits.find(
                            (u: StorageUnit) => u.id === product.storage_unit_id
                          )?.short || 't.'
                        : 't.'
                    }
                    onEdit={() => handleEditProduct(product.category_id)}
                    onDelete={() => handleDeleteProduct(product.category_id)}
                  />
                ))
              : null}
          </div>
        ) : (
          <div className="form">
            <div className="form-header">
              <Label
                title={true}
                label={
                  isEditMode
                    ? intl.formatMessage({ id: 'listings.edit_product_title' })
                    : intl.formatMessage({ id: 'listings.add_product_title' })
                }
              />
              <div onClick={handleFormCancel}>
                <Icon name={'global-close-icon'} type={'small'} />
              </div>
            </div>

            <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
              <Input
                className={'name-input'}
                name="name"
                label={intl.formatMessage({ id: 'listings.product_name' })}
              />
              <CategorySelector
                validations="required"
                admin
                mainCategoryValue={
                  editingProduct?.category?.route_path?.primary
                }
                subCategoryValue={
                  editingProduct?.category?.route_path?.secondary
                }
                subSubCategoryValue={
                  editingProduct?.category?.route_path?.tertiary
                }
              />

              <div className="row">
                <div className="quantity-and-unit">
                  <InputNumber
                    className={'quantity-input'}
                    validations="required"
                    name="required_quantity"
                    label={intl.formatMessage({
                      id: 'listings.required_quantity',
                    })}
                    controls={false}
                  />
                  <Select
                    value={selectedStorageUnit || undefined}
                    onChange={(value) => setSelectedStorageUnit(value)}
                    placeholder={''}
                    allowClear
                    optionLabelProp="label"
                  >
                    {storageUnits.map((unit: StorageUnit) => (
                      <SelectOption
                        key={unit.id}
                        value={unit.id}
                        label={unit.short || unit.name}
                      >
                        {unit.name} {unit.short ? `(${unit.short})` : ''}
                      </SelectOption>
                    ))}
                  </Select>
                </div>
                {allowSmallerQuantity && (
                  <div className="quantity-and-unit">
                    <InputNumber
                      className={'quantity-input'}
                      name="min_acceptable_offer_quantity"
                      label={intl.formatMessage({
                        id: 'listings.min_acceptable_offer_quantity',
                      })}
                      controls={false}
                      validations={'required'}
                    />
                    <Select
                      value={selectedStorageUnit || undefined}
                      onChange={(value) => setSelectedStorageUnit(value)}
                      placeholder={''}
                      allowClear
                      optionLabelProp="label"
                    >
                      {storageUnits.map((unit: StorageUnit) => (
                        <SelectOption
                          key={unit.id}
                          value={unit.id}
                          label={unit.short || unit.name}
                        >
                          {unit.name} {unit.short ? `(${unit.short})` : ''}
                        </SelectOption>
                      ))}
                    </Select>
                  </div>
                )}
              </div>

              <div className="row">
                <div className="allow-smaller-quantity">
                  <Checkbox
                    checked={allowSmallerQuantity}
                    onChange={(e) => {
                      setAllowSmallerQuantity(e.target.checked);
                    }}
                    label={intl.formatMessage({
                      id: 'listings.allow_smaller_quantity',
                    })}
                  />
                </div>
              </div>

              <div className="row">
                <div className="quality-marks">
                  <Label
                    label={intl.formatMessage({
                      id: 'listings.quality_marks_for_farm',
                    })}
                    title={true}
                  />
                  <div className="checkboxes">
                    <Checkbox
                      key={`quality-marks-${selectedQualityMarks.join(',')}`}
                      initialValue={selectedQualityMarks}
                      onChange={(values) => {
                        setSelectedQualityMarks(values);
                      }}
                      options={farmQualityMarks.map(
                        (qmark: FarmQualityMark) => ({
                          value: qmark.value,
                          label: qmark.label,
                          className: qmark.className,
                        })
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <Button
                  className="cancel-button"
                  label={intl.formatMessage({ id: 'general.cancel' })}
                  onClick={handleFormCancel}
                />
                <Button
                  className="add-button"
                  type={'primary'}
                  label={
                    isEditMode
                      ? intl.formatMessage({ id: 'general.update' })
                      : intl.formatMessage({ id: 'general.add' })
                  }
                  onClick={() => form.submit()}
                />
              </div>
            </Form>
          </div>
        )}
      </div>
    </StyledNewListingProductsForm>
  );
};
